import React, { useEffect, useState } from 'react';
import { Container, Typography, Grid, Box } from '@mui/material';
import { useKeycloak } from '../../services/KeycloakProvider';
import { HomePageCard, HomePageCardAction } from './HomePageCard';
import { mdiCheckCircleOutline } from '@mdi/js';
import './HomePage.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApiService } from '../../services/ApiServiceProvider';
import { ConsentStatus, EConsentResponse, ProlificInformationPost } from '../../services/ApiService';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Roles } from '../../Roles';
import { sites } from '../enrollment/models/AllSites';
import CollectProlificIdView from './CollectProlificIdView';

export const HomePage: React.FC = () => {
  const { roles } = useKeycloak();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const apiService = useApiService();
  const { userInfo } = useKeycloak();
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [consentStatus, setConsentStatus] = useState<ConsentStatus | null>(null);
  const [eConsent, setEConsent] = useState<EConsentResponse | null>(null);
  const [loadingEConsent, setLoadingEConsent] = useState<boolean>(false);
  const [open, setOpen] = useState(false);

  const hasDataCollectionRole = roles.includes(Roles.RemoteCollectionUser);
  const hasDashboardRole = roles.includes(Roles.DashboardUser);
  const hasDataCollectorRole = roles.includes(Roles.DataCollectorUser);
  const hasOnboardingRole = roles.includes(Roles.OnboardingUser);

  const checkConsentStatus = async (participantId: string, signal?: AbortSignal) => {
    setLoading(true);

    try {
      const consentInfo = await apiService.getParticipantConsentAndLanguage(participantId, signal);

      if (consentInfo) {
        const language = consentInfo.language || "English";
        if (language) {
          if (language === 'Spanish') {
            i18n.changeLanguage('es-419');
          } else {
            i18n.changeLanguage('en');
          }
        }

        setConsentStatus(consentInfo.consentStatus || 'Pending');

        // If participant is consented and it is Prolific 
        if (consentInfo.consentStatus === 'Consented') {
          if (consentInfo.isProlific === 'yes') {
            setLoading(false);
            // but doesn't have a Prolific ID, prompt them to enter it
            if (!consentInfo.prolificId) {
              setOpen(true);
            } else if (consentInfo.showProlificStudyId === 'yes') {
              navigate('/prolific-study-id', { state: { participantId } });
            }
          }
        }
      } else {
        navigate('/error', { state: { message: 'Not found.' } });
      }
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      if (error instanceof Error) {
        if (error.message.includes('Forbidden, you do not have permissions to access this resource')) {
          navigate('/error', { state: { message: `You don't have permissions to view this page` } });
        } else {
          navigate('/error', { state: { message: 'Failed to fetch participant data.' } });
          console.error('Error fetching participant data:', error);
        }
      } else {
        navigate('/error', { state: { message: 'Failed to fetch participant data.' } });
        console.error('Error fetching participant data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!participantId) { return; }

    const abortController = new AbortController();
    checkConsentStatus(participantId, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [participantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (consentStatus !== 'Pending' || !participantId) { return; }

    setLoadingEConsent(true);
    apiService.getEconsentSurveyLinks(participantId)
      .then(setEConsent)
      .catch(console.error)
      .then(() => setLoadingEConsent(false), () => setLoadingEConsent(false));
  }, [consentStatus, participantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userInfo) {
      if (userInfo.subjectId) {
        setParticipantId(userInfo.subjectId);
      }
    }
  }, [userInfo]);

  const handleAddProlificId = async (prolificId: string) => {
    if (!prolificId) {
      console.error("Missing Prolific ID.");
      return;
    }

    try {
      setLoading(true);
      const payload = { 
        prolificId
      } as ProlificInformationPost;

      await apiService.addProlificId(participantId!, payload);
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error adding Prolific ID:', error);
        navigate('/error', { state: { message: 'Failed to add Prolific ID.' + error.message } });
      }
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" className="homepage-container">
      <Box className="homepage-header">
        <Typography variant="h3" component="h1" gutterBottom id="page-heading" tabIndex={-1}>
          Welcome to B2Ai Voice
        </Typography>
      </Box>

      <Grid container spacing={4} justifyContent="center">
        {hasDataCollectionRole && (
          <Grid item xs={12} sm={6} md={4}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px" maxHeight="1rem">
                <LoadingSpinner size={30} />
              </Box>
            ) : (
              <HomePageCard
                title={t('Data Collection')}
                description={t('Begin your voice and audio data collection journey. Complete various tasks that will help advance voice as a biomarker of health. Make sure you are in a quiet place and ready to record a few audio clips.')}
                image="/assets/images/b2ai-voice-task.jpg"
                imageAlt={t('Start Data Collection')}

                actions={[
                  ...(consentStatus === 'Pending' ? [{
                    label: t('Complete eConsent'),
                    route: `/consent?status=${consentStatus}`,
                    loading: loadingEConsent,
                    disabled: !loadingEConsent && (!eConsent?.enabled || !eConsent?.surveyLinks?.length),
                    informationMessage: !loadingEConsent && (!eConsent?.enabled || !eConsent?.surveyLinks?.length) && eConsent?.site
                      ? t('The institution {{site}} does not support eConsent.', { site: sites.find(e => e.name === eConsent.site)?.displayName || eConsent.site })
                      : eConsent?.surveyLinks?.length && eConsent.surveyLinks.every(s => s.completed)
                      ? t('You have completed all consent surveys. Your submission is under review — someone will contact you once your consent status has been updated.')
                      : undefined,
                    informationIcon: eConsent?.surveyLinks?.length && eConsent.surveyLinks.every(s => s.completed)
                      ? mdiCheckCircleOutline
                      : undefined,
                    informationIconColor: eConsent?.surveyLinks?.length && eConsent.surveyLinks.every(s => s.completed)
                      ? 'success.main'
                      : undefined,
                  } as HomePageCardAction] : []),
                  {
                    label: t('Start Data Collection'),
                    route: '/data-collection/tasks',
                    disabled: consentStatus !== 'Consented',
                    informationMessage: consentStatus === 'Pending'
                      ? t('You need to provide consent to participate in the study. Your current consent status is: Pending')
                      : consentStatus === 'Withdrawn Consent'
                      ? t('Your consent has been withdrawn. Your current consent status is: Withdrawn Consent')
                      : undefined, // Only show message if not pending - if pending we want to encourage them to complete consent, if withdrawn we want to inform them they can no longer participate
                  }
                ]}
              />
            )}
          </Grid>
        )}

        {hasOnboardingRole && (
          <Grid item xs={12} sm={6} md={4}>
            <HomePageCard
              title="Onboarding"
              description="Access the onboarding materials to get started with the Bridge2AI Voice project."
              image="/assets/images/b2ai-onboarding.jpg"
              imageAlt="Onboarding Logo"
              actions={[{ label: 'Onboarding', route: '/onboarding' }]}
            />
          </Grid>
        )}

        {hasDataCollectorRole && (
          <Grid item xs={12} sm={6} md={4}>
            <HomePageCard
              title="REDCap"
              description="Access REDCap for data management, electronic data capture, and study coordination activities."
              image="/assets/images/redcap_logo.png"
              imageAlt="Go to REDCap"
              actions={[{ label: 'Go to REDCap', route: 'https://redcap.ctsc.weill.cornell.edu/' }]}
            />
          </Grid>
        )}

        {hasDashboardRole && (
          <Grid item xs={12} sm={6} md={4}>
            <HomePageCard
              title="Dashboard"
              description="Access the Bridge2AI Voice internal dashboard to view analytics, track progress, and manage your data collection activities."
              image="/assets/images/b2ai-dashboard.jpg"
              imageAlt="Dashboard Logo"
              actions={[{ label: 'Dashboard', route: '/dashboard' }]}
            />
          </Grid>
        )}
      </Grid>

      {!hasDataCollectionRole && !hasDashboardRole && !hasDataCollectorRole && (
        <Box className="homepage-no-access">
          <Typography variant="h6" component='div' color="textSecondary">
            You don't have access to any features yet. Please contact an administrator.
          </Typography>
        </Box>
      )}

      <CollectProlificIdView
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleAddProlificId}
      />
    </Container>
  );
}
